// Fix CL (2026-09-13): "Panel de configuración para mapear bloques/capas de AutoCAD con
// códigos de artículos del ERP" (CEN, 13/09). Cada regla dice qué material sale de cada
// capa o bloque del plano, cómo se mide (unidades o metros), con qué factor y desperdicio.
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Search, Map as MapIcon } from "lucide-react";
import { API } from "../../contexts/AuthContext";
import { MapeoRapidoModal } from "./PlanosComputoModal";

const opts = { withCredentials: true };

export default function MapeoPlanos() {
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState("");
  const [editar, setEditar] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/planos/mapeos`, opts);
      setReglas(Array.isArray(data) ? data : []);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cargar el mapeo"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const borrar = async (r) => {
    try {
      await axios.delete(`${API}/planos/mapeos/${r._id}`, opts);
      toast.success("Regla borrada");
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al borrar la regla"); }
  };

  const q = buscar.trim().toLowerCase();
  const filtradas = q
    ? reglas.filter(r => [r.patron, r.descripcion, r.material_codigo, r.seccion, r.capa]
      .some(v => (v || "").toLowerCase().includes(q)))
    : reglas;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="mapeo-planos">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Ingeniería</span>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mt-1 flex items-center gap-2">
            <MapIcon size={26} className="text-violet-600" /> Mapeo de Planos
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Qué material del ERP sale de cada capa o bloque del plano · lo usa el cómputo automático de Proyectos
          </p>
        </div>
        <button onClick={() => setEditar({})} className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="mapeo-nueva-btn">
          <Plus size={15} /> Nueva regla
        </button>
      </div>

      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder="Buscar capa, bloque, material o sección"
          className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm" data-testid="mapeo-buscar" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50/60 text-gray-400 uppercase">
            <tr>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Capa / bloque</th>
              <th className="text-left px-4 py-3">Material</th>
              <th className="text-left px-4 py-3">Código</th>
              <th className="text-left px-4 py-3">Medición</th>
              <th className="text-left px-4 py-3">Unidad</th>
              <th className="text-right px-4 py-3">Factor</th>
              <th className="text-right px-4 py-3">Desperdicio</th>
              <th className="text-left px-4 py-3">Sección</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody data-testid="mapeo-tabla">
            {loading ? <tr><td colSpan={10} className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-gray-400" /></td></tr>
              : filtradas.length === 0 ? <tr><td colSpan={10} className="py-10 text-center text-gray-400">
                {reglas.length ? "Sin coincidencias" : "Todavía no hay reglas de mapeo cargadas"}</td></tr>
                : filtradas.map(r => (
                  <tr key={r._id} className={`border-t border-gray-50 ${r.activo === false ? "opacity-50" : ""}`} data-testid={`mapeo-fila-${r._id}`}>
                    <td className="px-4 py-2">
                      <span className={`px-1.5 py-0.5 rounded font-bold ${r.tipo === "capa" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>{r.tipo}</span>
                    </td>
                    <td className="px-4 py-2 font-mono">{r.patron}{r.capa ? <span className="text-gray-400"> · {r.capa}</span> : null}</td>
                    <td className="px-4 py-2 text-gray-800">{r.descripcion}</td>
                    <td className="px-4 py-2 font-mono text-gray-500">{r.material_codigo || "—"}</td>
                    <td className="px-4 py-2">{r.medicion === "longitud" ? "Metros" : "Unidades"}</td>
                    <td className="px-4 py-2">{r.unidad}</td>
                    <td className="px-4 py-2 text-right">{r.factor}</td>
                    <td className="px-4 py-2 text-right">{r.desperdicio_pct ? `${r.desperdicio_pct}%` : "—"}</td>
                    <td className="px-4 py-2 text-gray-500">{r.seccion}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setEditar(r)} className="text-[#2E6DA4] hover:bg-blue-50 p-1.5 rounded-lg" data-testid={`mapeo-editar-${r._id}`}><Pencil size={13} /></button>
                      <button onClick={() => borrar(r)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg ml-1" data-testid={`mapeo-borrar-${r._id}`}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {editar && <MapeoRapidoModal inicial={editar} onClose={() => setEditar(null)} onSaved={cargar} />}
    </div>
  );
}
